import type {
  RegisterUserInput,
  RegistrationTerm,
} from "@app/types/user.types";
import axios, { AxiosResponse } from "axios";
import { ApiVersion } from "@app/constants/api.constants";
import { DATE_TIME_FORMAT_1 } from "@app/constants/date.constants";
import { ISOLocaleCode } from "@app/constants/route.constants";

import { api, CustomAxiosRequestConfig } from "@app/api/api";
import { ENDPOINTS } from "@app/api/endpoints";
import { AdminPermissions } from "@app/constants";
import { SetUserAdminPermissionsPayload } from "@app/types/adminPermissionsPayload.types";

export const getUser = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.user, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiGetUserNumericMeters = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.numericMeter);
};
export const getUserPermissions = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getUserPermissions, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const getTradeMarkData = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getTradeMarkData);
};

export const deleteUser = (): Promise<AxiosResponse> => {
  return api.delete(ENDPOINTS.user);
};

export const putUserSettings = (params: {
  suppressDataUsagePopup: boolean;
}): Promise<AxiosResponse> => {
  return api.put(ENDPOINTS.userSettings, params);
};

export const getReportsPrintLimit = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.reportsPrintLimit, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const getReportsMediaCount = (
  machineReportIds: string[],
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.reportsMediaCount, { machineReportIds }, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const getMachineManufacturers = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineManufacturers);
};

export const getMachineTypes = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.machineTypes);
};

export const getSampleItemsMediaData = (): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getSampleItemsMediaData);
};

export const getTOUVersionMaster = (
  residenceCountryCode: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getTOUVersionMaster(residenceCountryCode), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const getTOUVersionMasterAll = (params: any): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getTOUVersionMasterAll, {
    params,
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiDeleteTOUVersion = (ids: string[]): Promise<AxiosResponse> => {
  return api.delete(ENDPOINTS.deleteTOUVersion, { data: { ids } });
};

export const apiPostTOUVersionMaster = (
  payload: any,
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.postTOUVersionMaster, payload);
};

export const apiPutTOUVersionMaster = (
  payload: any,
): Promise<AxiosResponse> => {
  return api.put(ENDPOINTS.updateTOUVersionMaster, payload);
};

export const apiPostTOURecordHistory = (
  payload: any,
): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.postTOURecordHistory, payload);
};

export const apiPutUser = (payload: any): Promise<AxiosResponse> => {
  return api.put(ENDPOINTS.putUser, payload);
};

export const apiSearchUsers = (search: string): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.apiSearchUsers(search), {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiGetUserAdminPermissions = (
  userId?: string,
): Promise<AxiosResponse> => {
  return api.get(ENDPOINTS.getUserAdminPermissions, {
    params: userId ? { userId } : undefined,
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

export const apiRestoreUser = (userId: string): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.restoreUser(userId));
};

export const apiPutUserAdminPermissions = (
  userId: string | undefined,
  permissions: Partial<Record<AdminPermissions, boolean>>,
): Promise<AxiosResponse> => {
  if (!userId) throw new Error("userId is required");

  const payload: SetUserAdminPermissionsPayload = { userId, permissions };

  return api.put(ENDPOINTS.putUserAdminPermissions, payload);
};

export const apiCheckBlockedCountry = (): Promise<AxiosResponse> => {
  return api.post(ENDPOINTS.checkBlockedCountry, {
    skipGlobalLoader: true,
  } as CustomAxiosRequestConfig);
};

// Onboarding runs outside Features and its user-dependent interceptors.
const registrationApi = axios.create({
  baseURL: api.defaults.baseURL,
  timeout: api.defaults.timeout,
  headers: { "Content-Type": "application/json", platform: "WEBAPP" },
});
const headers = (token: string, language: string) => ({
  "X-Access-Token": `Bearer ${token}`,
  "x-lang": language,
});
export function registerUser(
  input: RegisterUserInput,
  token: string,
  language: string,
): Promise<AxiosResponse<{ data: Record<string, unknown>; meta: object }>> {
  const requestedLocale = (input.isoLocaleCode || language || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  const locale =
    Object.values(ISOLocaleCode).find((value) => value === requestedLocale) ||
    ISOLocaleCode.EN;
  // Enforce required nested fields at the HTTP boundary, including callers
  // that pass nullable locale values directly from the terms API.
  const payload: RegisterUserInput = {
    ...input,
    timeFormat: input.timeFormat?.trim() || DATE_TIME_FORMAT_1,
    isoLocaleCode: locale,
    touPrivacy: input.touPrivacy.map((term) => ({
      ...term,
      isoLocaleCode: term.isoLocaleCode?.trim() || locale,
    })),
  };
  return registrationApi.post(
    process.env.REACT_APP_REGISTER_USER_ENDPOINT ||
      `${ApiVersion.LATEST}/users`,
    payload,
    { headers: headers(token, locale) },
  );
}
export async function getRegistrationTerms(
  country: string,
  token: string,
  language: string,
) {
  const response = await registrationApi.get(
    `${ApiVersion.LATEST}/users/me/touVersionMaster/${country}`,
    { headers: headers(token, language) },
  );
  return response.data.data as RegistrationTerm[];
}
